/**
 * pt 明細モーダル (当月pt / 未対応pt) 共通の表示部品。
 * 子チケット単位の pt 明細テーブル・Backlog リンク・スタイル定数・スピナーを一元化し、
 * CurrentMonthPtDetailModal と OpenPtDetailModal の同型実装を排除する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 子チケットpt 明細テーブルと Backlog リンクの共通表示部品
 * @graph-connects none
 */
import { useTranslation } from 'react-i18next';
import { formatNum } from './combat-power/combat-power-utils.js';
import { BACKLOG_BASE } from './project/index.js';

/** 子チケットpt 明細 1 行 (処理中/未対応 共通の shape) @graph-connects none */
export interface PtDetailRow {
  /** チケットキー (Backlog) */
  issueKey: string;
  /** 要約 */
  summary: string;
  /** チケット種別名 (未設定なら null) */
  issueTypeName: string | null;
  /** pt (処理中=実績pt / 未対応=予定pt) */
  point: number;
}

/** @graph-connects none */
export const TH_CLASS =
  'py-2 px-3 text-left text-[11px] font-semibold text-[var(--color-text-muted)] whitespace-nowrap';
/** @graph-connects none */
export const TD_CLASS = 'py-2 px-3 text-sm text-[var(--color-text-primary)]';

/** Backlog 票へのリンク @graph-connects none */
export function IssueLink({ issueKey }: { issueKey: string }) {
  return (
    <a
      href={`${BACKLOG_BASE}${issueKey}`}
      target="_blank"
      rel="noopener noreferrer"
      className="font-mono text-xs text-[var(--color-accent-primary)] hover:underline whitespace-nowrap"
    >
      {issueKey}
    </a>
  );
}

/** ローディングスピナー @graph-connects none */
export function SectionSpinner() {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      aria-label={t('members.quarterlyKpi.ptDetail.loading')}
      className="h-16 flex items-center justify-center"
    >
      <div className="loading-spinner" />
    </div>
  );
}

/** @graph-connects none */
interface PtDetailTableProps {
  /** 明細行 */
  rows: PtDetailRow[];
  /** テーブルラッパの className (余白差の吸収用) */
  wrapperClassName?: string;
}

/**
 * 子チケットpt 明細テーブル (種別・要約・pt)。処理中/未対応で共通。
 * @graph-connects none
 */
export function PtDetailTable({ rows, wrapperClassName = 'overflow-x-auto' }: PtDetailTableProps) {
  const { t } = useTranslation();
  return (
    <div className={wrapperClassName}>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-[var(--color-border)]">
            <th className={TH_CLASS}>{t('members.quarterlyKpi.ptDetail.colIssue')}</th>
            <th className={`${TH_CLASS} w-full`}>
              {t('members.quarterlyKpi.ptDetail.colSummary')}
            </th>
            <th className={TH_CLASS}>{t('members.quarterlyKpi.ptDetail.colType')}</th>
            <th className={`${TH_CLASS} text-right`}>pt</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d) => (
            <tr
              key={d.issueKey}
              className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)]"
            >
              <td className={TD_CLASS}>
                <IssueLink issueKey={d.issueKey} />
              </td>
              <td className={TD_CLASS}>{d.summary}</td>
              <td
                className={`${TD_CLASS} text-xs text-[var(--color-text-secondary)] whitespace-nowrap`}
              >
                {d.issueTypeName ?? '-'}
              </td>
              <td className={`${TD_CLASS} text-right font-medium whitespace-nowrap`}>
                {formatNum(d.point, 1)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
